/**
 * Consent Field Types and Utilities
 *
 * Defines type shapes and factory functions for creating and normalizing
 * consent snapshots during form submission and loading.
 */

import {
  CONSENT_TYPES,
  getAllConsentFieldsForVersion,
  getConsentVersionAtDate,
} from "./consentVersions";

// ============================================================================
// Type Definitions (JSDoc)
// ============================================================================

/**
 * @typedef {Object} ConsentVersion
 * @property {string} versionId - Version identifier (e.g., '1.0')
 * @property {string} validFrom - ISO date when this version became active (e.g., '2026-01-01')
 * @property {string|null} validTo - ISO date when this version expired, or null if current
 * @property {Array<string>} fields - All field names this version captures
 * @property {Array<string>} checkboxFields - Boolean field names
 * @property {Array<string>} textFields - Text/textarea field names
 * @property {string|null} signatureField - Signature field name or null if no signature
 */

/**
 * @typedef {Object} ConsentSnapshot
 * @property {string} consentType - The CONSENT_TYPES constant
 * @property {string} versionId - The version that was active at submission
 * @property {string} submittedAt - ISO timestamp of submission
 * @property {Object} answers - Normalized answer values keyed by field name
 * @property {Object} metadata - Additional metadata (future use)
 *
 * @example
 *   {
 *     consentType: 'ACUPUNCTURE',
 *     versionId: '1.0',
 *     submittedAt: '2026-05-14T10:30:00Z',
 *     answers: {
 *       consentAcupuncture: true,
 *       consentBleedEasily: false,
 *       consentPregnant: false,
 *       ...
 *     },
 *     metadata: {}
 *   }
 */

/**
 * @typedef {Object} ConsentResponse
 * @property {boolean|string|null} [fieldName] - Field names are keys, values are the answers
 *
 * @example
 *   {
 *     consentAcupuncture: true,
 *     consentBleedEasily: false,
 *     sigAcu: 'data:image/png;base64,...'
 *   }
 */

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a normalized consent snapshot for a form submission.
 * Captures the version that was active at submission time and extracts
 * only the fields relevant to that version.
 *
 * @param {string} consentType - One of CONSENT_TYPES
 * @param {Object} stateValues - Current form state with all field values
 * @param {string|Date} [submittedAt] - Submission timestamp (defaults to now)
 * @returns {ConsentSnapshot} Normalized snapshot for storage
 *
 * @example
 *   const snapshot = createConsentSnapshot('ACUPUNCTURE', formState, new Date());
 */
export function createConsentSnapshot(
  consentType,
  stateValues,
  submittedAt = new Date().toISOString(),
) {
  if (
    !CONSENT_TYPES[
      Object.keys(CONSENT_TYPES).find((k) => CONSENT_TYPES[k] === consentType)
    ]
  ) {
    throw new Error(`Invalid consent type: ${consentType}`);
  }

  // Determine which version was active at submission time
  const version = getConsentVersionAtDate(consentType, submittedAt);
  if (!version) {
    throw new Error(
      `No active consent version for ${consentType} on ${submittedAt}`,
    );
  }

  // Extract only the fields relevant to this version
  const answers = {};
  version.fields.forEach((fieldName) => {
    if (fieldName in stateValues) {
      answers[fieldName] = stateValues[fieldName];
    }
  });

  return {
    consentType,
    versionId: version.versionId,
    submittedAt:
      submittedAt instanceof Date ? submittedAt.toISOString() : submittedAt,
    answers,
    metadata: {},
  };
}

/**
 * Convert legacy flat consent fields into a modern snapshot shape.
 * Used for backward compatibility when loading old patient records
 * that don't have the new consentVersions/consentAnswers structure.
 *
 * @param {string} consentType - One of CONSENT_TYPES
 * @param {Object} legacyData - Old-style flat fields from the database
 * @param {string|Date} [assumedSubmittedAt] - Date to assume for version lookup
 * @returns {ConsentSnapshot} Normalized snapshot in modern format
 *
 * @example
 *   const snapshot = legacyFieldsToSnapshot('ACUPUNCTURE', {
 *     consentAcupuncture: true,
 *     consentBleedEasily: false,
 *     sigAcu: 'data:image/png...'
 *   }, '2025-06-01');
 */
export function legacyFieldsToSnapshot(
  consentType,
  legacyData,
  assumedSubmittedAt = "2026-01-01",
) {
  if (
    !CONSENT_TYPES[
      Object.keys(CONSENT_TYPES).find((k) => CONSENT_TYPES[k] === consentType)
    ]
  ) {
    throw new Error(`Invalid consent type: ${consentType}`);
  }

  // Determine which version was "current" at the assumed submission date
  const version = getConsentVersionAtDate(consentType, assumedSubmittedAt);
  if (!version) {
    throw new Error(
      `No active consent version for ${consentType} on ${assumedSubmittedAt}`,
    );
  }

  // Extract relevant fields from legacy data
  const answers = {};
  version.fields.forEach((fieldName) => {
    if (fieldName in legacyData) {
      answers[fieldName] = legacyData[fieldName];
    }
  });

  return {
    consentType,
    versionId: version.versionId,
    submittedAt:
      assumedSubmittedAt instanceof Date
        ? assumedSubmittedAt.toISOString()
        : assumedSubmittedAt,
    answers,
    metadata: {
      migratedFromLegacy: true,
    },
  };
}

/**
 * Get the field definitions for a consent snapshot.
 * Useful for rendering forms with the correct field set.
 *
 * @param {ConsentSnapshot} snapshot - A consent snapshot object
 * @returns {Array<string>} List of field names for this version
 */
export function getFieldsForSnapshot(snapshot) {
  return getAllConsentFieldsForVersion(
    snapshot.consentType,
    snapshot.versionId,
  );
}

/**
 * Validate that all required fields are present in a consent snapshot.
 *
 * @param {ConsentSnapshot} snapshot - A consent snapshot to validate
 * @returns {Object} { valid: boolean, errors: Array<string> }
 */
export function validateConsentSnapshot(snapshot) {
  const errors = [];

  if (!snapshot.consentType) {
    errors.push("Missing consentType");
  }
  if (!snapshot.versionId) {
    errors.push("Missing versionId");
  }
  if (!snapshot.submittedAt) {
    errors.push("Missing submittedAt");
  }
  if (!snapshot.answers || typeof snapshot.answers !== "object") {
    errors.push("Invalid or missing answers object");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
